import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from './Toast';
import { handleError } from '../utils/errorHandler';
import './BugReport.css';

const BugReport = ({ onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    severity: 'medium',
    page: 'N/A',
    browserInfo: typeof navigator !== 'undefined' ? navigator.userAgent : ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bugCategories = [
    { value: 'general', label: 'bugReport.categoryGeneral' },
    { value: 'ui', label: 'bugReport.categoryUI' },
    { value: 'booking', label: 'bugReport.categoryBooking' },
    { value: 'payment', label: 'bugReport.categoryPayment' },
    { value: 'search', label: 'bugReport.categorySearch' },
    { value: 'map', label: 'bugReport.categoryMap' },
    { value: 'notification', label: 'bugReport.categoryNotification' },
    { value: 'authentication', label: 'bugReport.categoryAuthentication' },
    { value: 'performance', label: 'bugReport.categoryPerformance' },
    { value: 'mobile', label: 'bugReport.categoryMobile' },
    { value: 'other', label: 'bugReport.categoryOther' }
  ];

  const severityOptions = [
    { value: 'low', label: 'bugReport.severityLow' },
    { value: 'medium', label: 'bugReport.severityMedium' },
    { value: 'high', label: 'bugReport.severityHigh' },
    { value: 'critical', label: 'bugReport.severityCritical' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError(t('bugReport.titleRequired'));
      return false;
    }
    if (formData.title.trim().length < 5) {
      setError(t('bugReport.titleTooShort'));
      return false;
    }
    if (!formData.description.trim()) {
      setError(t('bugReport.descriptionRequired'));
      return false;
    }
    if (formData.description.trim().length < 20) {
      setError(t('bugReport.descriptionTooShort'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const { data, error: insertError } = await supabase
        .from('bug_reports')
        .insert([{
          user_id: user?.id || null,
          title: formData.title.trim(),
          description: formData.description.trim(),
          category: formData.category,
          severity: formData.severity,
          page_url: formData.page,
          browser_info: formData.browserInfo,
          status: 'open',
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      showToast(t('bugReport.reportedSuccessfully'), 'success', 4000);
      onClose();
    } catch (err) {
      handleError(err, 'Submitting bug report');
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bug-report-overlay">
      <div className="bug-report-modal">
        <button className="close-button" onClick={onClose}>×</button>

        <div className="report-header">
          <h2>🐛 {t('bugReport.title')}</h2>
          <p className="subtitle">{t('bugReport.subtitle')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="bug-form">
          {/* Category */}
          <div className="form-group">
            <label htmlFor="category">
              {t('bugReport.category')} *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              {bugCategories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {t(cat.label)}
                </option>
              ))}
            </select>
          </div>

          {/* Severity */}
          <div className="form-group">
            <label htmlFor="severity">
              {t('bugReport.severity')} *
            </label>
            <select
              id="severity"
              name="severity"
              value={formData.severity}
              onChange={handleChange}
              required
            >
              {severityOptions.map(sev => (
                <option key={sev.value} value={sev.value}>
                  {t(sev.label)}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">
              {t('bugReport.titleLabel')} *
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder={t('bugReport.titlePlaceholder')}
              maxLength="100"
              required
            />
            <small className="char-count">
              {formData.title.length}/100
            </small>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">
              {t('bugReport.descriptionLabel')} *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('bugReport.descriptionPlaceholder')}
              rows="6"
              maxLength="1000"
              required
            />
            <small className="char-count">
              {formData.description.length}/1000
            </small>
          </div>

          {/* Page Info */}
          <div className="form-group">
            <label htmlFor="page">
              {t('bugReport.pageLabel')}
            </label>
            <input
              id="page"
              type="text"
              name="page"
              value={formData.page}
              onChange={handleChange}
              placeholder={t('bugReport.pagePlaceholder')}
              maxLength="200"
            />
            <small>{t('bugReport.pageHint')}</small>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <p>
              ℹ️ {t('bugReport.info')}
            </p>
          </div>

          {/* Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? t('bugReport.submitting') : t('bugReport.submitReport')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReport;
